import React, { useId } from 'react';
import './HeroSolverDiagram.css';

/* ------------------------------------------------------------------ *
 * Layout knobs (all in viewBox coordinate space)
 * ------------------------------------------------------------------ */
const VB_W = 1200;
const VB_H = 560;

const CORE = { x: 600, y: 304 };
const HEX_R = 92;

const TILE = 58;          // input icon tile size
const TILE_X = 80;        // input column left edge
const TILE_GAP = 20;      // vertical gap between input tiles
const ICON_PAD = 11;      // padding of the icon image inside its tile

const CARD = { x: 920, w: 250, h: 300 };  // output build card
const CARD_PAD = 16;
const RENDER_H = 150;     // skin-render area height
const ITEM = 42;          // output item tile size
const ITEM_GAP_X = 10;
const ITEM_GAP_Y = 12;

/* ------------------------------------------------------------------ *
 * Content — real dofgen assets from /public/img
 * ------------------------------------------------------------------ */
const INPUTS = [
  { key: 'forgelance', src: '/img/class_forgelance.png', label: 'Forgelance' },
  { key: 'water', src: '/img/stat_do_eau.png', label: 'Water damage' },
  { key: 'air', src: '/img/stat_do_air.png', label: 'Air damage' },
  { key: 'earth', src: '/img/stat_do_terre.png', label: 'Earth damage' },
  { key: 'crit', src: '/img/stat_do_critique.png', label: 'Crit damage' },
  { key: 'range', src: '/img/stat_po.png', label: 'Range' },
];

const ITEM_ICONS = [
  'helmet_0', 'epee', 'arc', 'baton',
  'dagues', 'hache', 'familier', 'dragodinde',
];

// The three column headings drawn above the diagram — the only visible copy here.
const TXT_CONSTRAINTS = { fr: 'CRITÈRES', en: 'CONSTRAINTS', de: 'KRITERIEN', es: 'CRITERIOS', pt: 'CRITÉRIOS' };
const TXT_AI_SOLVER = { fr: 'SOLVEUR IA', en: 'AI SOLVER', de: 'KI-SOLVER', es: 'SOLVER IA', pt: 'SOLVER IA' };
const TXT_OPTIMIZED_BUILD = { fr: 'STUFF OPTIMISÉ', en: 'OPTIMIZED BUILD', de: 'OPTIMIERTER BUILD', es: 'BUILD ÓPTIMA', pt: 'BUILD IDEAL' };

/* ------------------------------------------------------------------ *
 * Derived geometry
 * ------------------------------------------------------------------ */
const SIN60 = Math.sin(Math.PI / 3);
const HEX_POINTS = [
  [CORE.x, CORE.y - HEX_R],
  [CORE.x + HEX_R * SIN60, CORE.y - HEX_R / 2],
  [CORE.x + HEX_R * SIN60, CORE.y + HEX_R / 2],
  [CORE.x, CORE.y + HEX_R],
  [CORE.x - HEX_R * SIN60, CORE.y + HEX_R / 2],
  [CORE.x - HEX_R * SIN60, CORE.y - HEX_R / 2],
].map((p) => p.map((n) => Math.round(n * 100) / 100).join(',')).join(' ');

const LEFT_FACE_X = CORE.x - HEX_R * SIN60;
const RIGHT_FACE_X = CORE.x + HEX_R * SIN60;
const MERGE = { x: Math.round(LEFT_FACE_X), y: CORE.y };

const COL_H = INPUTS.length * TILE + (INPUTS.length - 1) * TILE_GAP;
const COL_START_Y = CORE.y - COL_H / 2;
const tileTop = (i) => COL_START_Y + i * (TILE + TILE_GAP);
const tileCenterY = (i) => tileTop(i) + TILE / 2;

const CARD_Y = CORE.y - CARD.h / 2;

export default function HeroSolverDiagram({ className = '', lang = 'en' }) {
  const rid = useId().replace(/[:]/g, '');
  const glowId = `hsd-glow-${rid}`;

  return (
    <svg
      className={`hero-solver-diagram ${className}`}
      viewBox={`0 0 ${VB_W} ${VB_H}`}
      role="img"
      aria-label="dofgen turns your build constraints into an AI-optimized equipment set"
      xmlns="http://www.w3.org/2000/svg"
    >
      <title>How dofgen works</title>
      <desc>
        A column of constraint inputs — Forgelance, water, air and earth damage,
        crit damage and range — feed into the dofgen AI solver, which outputs an
        optimized build with a character render and item set.
      </desc>

      <defs>
        <radialGradient id={glowId} cx="50%" cy="50%" r="50%">
          <stop offset="0%" className="hsd-glow-stop-in" />
          <stop offset="62%" className="hsd-glow-stop-mid" />
          <stop offset="100%" className="hsd-glow-stop-out" />
        </radialGradient>
      </defs>

      {/* section labels */}
      <text className="hsd-label" x={TILE_X + TILE / 2} y="34" textAnchor="middle">
        {TXT_CONSTRAINTS[lang] || TXT_CONSTRAINTS.en}
      </text>
      <text className="hsd-label" x={CORE.x} y="34" textAnchor="middle">
        {TXT_AI_SOLVER[lang] || TXT_AI_SOLVER.en}
      </text>
      <text className="hsd-label" x={CARD.x + CARD.w / 2} y="34" textAnchor="middle">
        {TXT_OPTIMIZED_BUILD[lang] || TXT_OPTIMIZED_BUILD.en}
      </text>

      {/* glow behind core */}
      <circle
        className="hsd-glow"
        cx={CORE.x}
        cy={CORE.y}
        r={HEX_R * 1.7}
        fill={`url(#${glowId})`}
      />

      {/* connectors: inputs -> core (animated flow) */}
      <g className="hsd-connectors">
        {INPUTS.map((inp, i) => {
          const cy = tileCenterY(i);
          const x0 = TILE_X + TILE;
          const d = `M ${x0} ${cy} C ${x0 + 180} ${cy} ${MERGE.x - 90} ${MERGE.y} ${MERGE.x} ${MERGE.y}`;
          return <path key={inp.key} className="hsd-conn hsd-flow" d={d} />;
        })}
        <circle className="hsd-merge" cx={MERGE.x} cy={MERGE.y} r="3.5" />
      </g>

      {/* connector: core -> output */}
      <path
        className="hsd-conn hsd-conn--out hsd-flow"
        d={`M ${RIGHT_FACE_X} ${CORE.y} L ${CARD.x - 14} ${CORE.y}`}
      />
      <path
        className="hsd-arrowhead"
        d={`M ${CARD.x - 2} ${CORE.y} L ${CARD.x - 16} ${CORE.y - 8} L ${CARD.x - 16} ${CORE.y + 8} Z`}
      />

      {/* input icon tiles (icons only) */}
      {INPUTS.map((inp, i) => (
        <g key={inp.key}>
          <rect
            className="hsd-tile"
            x={TILE_X}
            y={tileTop(i)}
            width={TILE}
            height={TILE}
            rx="16"
          />
          <image
            href={inp.src}
            x={TILE_X + ICON_PAD}
            y={tileTop(i) + ICON_PAD}
            width={TILE - ICON_PAD * 2}
            height={TILE - ICON_PAD * 2}
            preserveAspectRatio="xMidYMid meet"
          >
            <title>{inp.label}</title>
          </image>
        </g>
      ))}

      {/* core */}
      <polygon className="hsd-core-hex" points={HEX_POINTS} />
      <image
        href="/img/dofgen_logo.svg"
        x={CORE.x - 62}
        y={CORE.y - 20}
        width="124"
        height="40"
        preserveAspectRatio="xMidYMid meet"
      />
      {/* sparkles */}
      <path
        className="hsd-sparkle hsd-sparkle--1"
        d="M0 -7 L2.4 -2.4 7 0 2.4 2.4 0 7 -2.4 2.4 -7 0 -2.4 -2.4 Z"
        transform={`translate(${RIGHT_FACE_X - 6} ${CORE.y - HEX_R + 6})`}
      />
      <path
        className="hsd-sparkle hsd-sparkle--2"
        d="M0 -5 L1.7 -1.7 5 0 1.7 1.7 0 5 -1.7 1.7 -5 0 -1.7 -1.7 Z"
        transform={`translate(${LEFT_FACE_X + 8} ${CORE.y + HEX_R - 8})`}
      />

      {/* output build card */}
      <g className="hsd-card">
        <rect
          className="hsd-card-bg"
          x={CARD.x}
          y={CARD_Y}
          width={CARD.w}
          height={CARD.h}
          rx="18"
        />
        {/* skin render */}
        <rect
          className="hsd-render-bg"
          x={CARD.x + CARD_PAD}
          y={CARD_Y + CARD_PAD}
          width={CARD.w - CARD_PAD * 2}
          height={RENDER_H}
          rx="12"
        />
        <image
          href="/img/class_forgelance_m.png"
          x={CARD.x + CARD_PAD}
          y={CARD_Y + CARD_PAD - 6}
          width={CARD.w - CARD_PAD * 2}
          height={RENDER_H + 6}
          preserveAspectRatio="xMidYMid meet"
        />
        {/* item grid 4 x 2 */}
        {ITEM_ICONS.map((name, idx) => {
          const c = idx % 4;
          const r = Math.floor(idx / 4);
          const gridW = 4 * ITEM + 3 * ITEM_GAP_X;
          const gx = CARD.x + (CARD.w - gridW) / 2 + c * (ITEM + ITEM_GAP_X);
          const gy = CARD_Y + CARD_PAD + RENDER_H + 28 + r * (ITEM + ITEM_GAP_Y);
          return (
            <g key={name}>
              <rect className="hsd-item" x={gx} y={gy} width={ITEM} height={ITEM} rx="9" />
              <image
                href={`/img/${name}.png`}
                x={gx + 7}
                y={gy + 7}
                width={ITEM - 14}
                height={ITEM - 14}
                preserveAspectRatio="xMidYMid meet"
              />
            </g>
          );
        })}
      </g>
    </svg>
  );
}
